import { openEraseDialog } from '../erase-control.js';
import {
  type Color,
  type DesignAreaAnswer,
  type DesignImageEffect,
  type DesignLayer,
  type DesignerPattern,
} from '@pz/scene';
import { el, note, switchRow } from '../panels.js';
import { canDelete, run } from '../commands.js';
import { renderCropControl } from '../crop-control.js';
import { createLookGrid } from '../effects-panel.js';
import { renderImagePanel, toggleRemoveBackground, endReplace } from '../image-panel.js';
import {
  anchorVOf,
  canHaveFixedWidth,
  isFixedTextBox,
  minWrapWidthMm,
  setTextBox,
  setTextBoxWidth,
} from '../text-box.js';
import { fillText } from '../texts.js';
import type { LayerPatch } from '../state.js';
import { phoneIconSvg, type PhoneIconName } from './icons.js';
import { createPhoneStepper } from './stepper.js';
import { safeColorBlock, safeFontPicker } from './controls-safe.js';
import type { PhoneEnv, SubPanelSpec } from './types-phone.js';
import type { PhoneGridItem } from './property-grid.js';
import {
  BEND_RANGE,
  CROP_STRAIGHTEN_RANGE,
  CROP_ZOOM_RANGE,
  FILTER_AMOUNT_RANGE,
  LINE_SPACING_RANGE,
  OPACITY_RANGE,
  PATTERN_GAP_RANGE,
  ROTATION_RANGE,
  SHADOW_ANGLE_RANGE,
  SHADOW_BLUR_RANGE,
  SHADOW_OFFSET_RANGE,
  SHAPE_STROKE_RANGE,
  STROKE_RANGE,
  TRACKING_RANGE,
  bendToUi,
  cropZoomToUi,
  filterAmountToUi,
  opacityToUi,
  rotationToUi,
  sizeRange,
  strokeToUi,
  wrapWidthRange,
  trackingToUi,
  uiToBend,
  uiToCropZoom,
  uiToFilterAmount,
  uiToOpacity,
  uiToRotation,
  uiToStroke,
  uiToTracking,
} from './values.js';

/**
 * The phone's property sub-panels: "OTKAŽI | Naslov | PRIMENI" over a stepper,
 * a swatch grid or a row of options — Zazzle's mobile pattern, one panel per
 * grid cell.
 *
 * Two rules run through all of them.
 *
 * Live preview, one undo step. Every edit is dispatched with the SAME tag, so
 * the store coalesces a whole panel session into a single history entry (see
 * `commit` in state.ts). Otkaži then undoes exactly that entry and the design
 * is back where it was; Primeni simply closes and the entry stands.
 *
 * Nothing is "applied" only at the end. A shopper who drags the size slider
 * sees the text grow while they drag, because the design IS the preview; the
 * only thing the Primeni button decides is whether the change survives.
 */

type Item = Exclude<PhoneGridItem, 'divider'>;

/** Dispatches at most this often while a slider is being dragged. */
const LIVE_MS = 70;

/** The one place a grid cell turns into an action or a panel. */
export function runGridItem(env: PhoneEnv, item: Item): void {
  const ctx = env.ctx;
  const layer = ctx.selected();

  // Cells that are not about the selected object at all.
  if (item === 'add') return env.openAdd();
  if (item === 'preview') return env.openReview();
  if (item === 'layers') return env.openAdd('layers');
  if (item === 'background') return env.openAdd('background');
  if (item === 'templates') return env.openAdd('templates');
  if (!layer) return;
  if (item === 'eraser' && layer.kind === 'image') { env.closeSheet(); openEraseDialog(ctx, layer.id); return; }

  // Cells that act at once — Zazzle opens no panel for these either.
  switch (item) {
    case 'delete':
      // The cell is not drawn for a permanent or non-interactive layer; the
      // guard is for a design that changed under an open grid.
      if (!canDelete(layer)) return;
      ctx.selection.delete(layer.id);
      run(ctx, { op: 'delete', ids: [layer.id] });
      env.showSub(null);
      return;
    case 'duplicate':
      run(ctx, { op: 'duplicate', ids: [layer.id] });
      return;
    case 'orientation':
      if (layer.kind !== 'text') return;
      run(ctx, {
        op: 'orientation',
        ids: [layer.id],
        orientation: layer.orientation === 'vertical' ? 'horizontal' : 'vertical',
      });
      env.refreshGrid();
      return;
    case 'fitZone':
      run(ctx, { op: 'fitToZone', ids: [layer.id], mode: 'fit' });
      return;
    case 'fillZone':
      run(ctx, { op: 'fitToZone', ids: [layer.id], mode: 'fill' });
      return;
    case 'cutout':
      if (layer.kind !== 'image') return;
      // One implementation for both shells: the cut-out is a second asset
      // registered under a derived id, and the layer only carries the effect.
      ctx.toast(ctx.texts.cutoutWorking, 'info');
      void toggleRemoveBackground(ctx, layer).then(() => env.refreshGrid());
      return;
    default:
      break;
  }

  const panel = subPanelFor(env, item, layer);
  if (panel) env.showSub(panel);
}

/** Which panel a cell opens; `null` when the cell does not suit this layer. */
function subPanelFor(env: PhoneEnv, item: Item, layer: DesignLayer): SubPanelSpec | null {
  const t = env.texts;
  switch (item) {
    case 'edit':
      return layer.kind === 'text' ? textPanel(env, layer.id) : null;
    case 'font':
      return layer.kind === 'text' ? fontPanel(env, layer.id) : null;
    case 'size':
      return layer.kind === 'text' ? sizePanel(env, layer.id) : null;
    case 'color':
      return layer.kind === 'text' ? colorPanel(env, layer.id) : null;
    case 'align':
      return layer.kind === 'text' ? alignPanel(env, layer.id) : null;
    case 'textWidth':
      return canHaveFixedWidth(layer) ? textWidthPanel(env, layer.id) : null;
    case 'spacing':
      return layer.kind === 'text' ? numberPanel(env, layer.id, {
        id: item,
        title: t.titleSpacing,
        range: TRACKING_RANGE,
        read: (current) => (current.kind === 'text' ? trackingToUi(current.tracking) : 0),
        write: (value) => ({ tracking: uiToTracking(value) }),
      }) : null;
    case 'lineHeight':
      return layer.kind === 'text' ? numberPanel(env, layer.id, {
        id: item,
        title: t.titleLineHeight,
        range: LINE_SPACING_RANGE,
        read: (current) => (current.kind === 'text' ? current.lineSpacing ?? 1.15 : 1),
        write: (value) => ({ lineSpacing: value }),
      }) : null;
    case 'curve':
      return layer.kind === 'text' ? numberPanel(env, layer.id, {
        id: item,
        title: t.titleCurve,
        range: BEND_RANGE,
        read: (current) => (current.kind === 'text' ? bendToUi(current.bend) : 0),
        write: (value) => ({ bend: uiToBend(value) }),
      }) : null;
    case 'textStroke':
      return layer.kind === 'text' ? strokePanel(env, layer.id) : null;
    case 'shadow':
      return layer.kind === 'text' ? shadowPanel(env, layer.id) : null;
    case 'rotate':
      return rotatePanel(env, layer.id);
    case 'flip':
      return flipPanel(env, layer.id);
    case 'arrange':
      return arrangePanel(env, layer.id);
    case 'pattern':
      return patternPanel(env, layer.id);
    case 'opacity':
      return numberPanel(env, layer.id, {
        id: item,
        title: t.titleOpacity,
        range: OPACITY_RANGE,
        read: (current) => opacityToUi(current.opacity),
        write: (value) => ({ opacity: uiToOpacity(value) }),
      });
    case 'replace':
      return layer.kind === 'image' ? replacePanel(env, layer.id) : null;
    case 'crop':
      return layer.kind === 'image' ? cropPanel(env, layer.id) : null;
    case 'filters':
      return layer.kind === 'image' ? filtersPanel(env, layer.id) : null;
    case 'shapeFill':
      return layer.kind === 'shape' ? shapeFillPanel(env, layer.id) : null;
    case 'shapeLine':
      return layer.kind === 'shape' ? shapeLinePanel(env, layer.id) : null;
    case 'shapeWidth':
      return layer.kind === 'shape' ? shapeWidthPanel(env, layer.id) : null;
    case 'recolor':
      return layer.kind === 'artwork' ? recolorPanel(env, layer.id) : null;
    case 'qrEdit':
      return layer.kind === 'qr' ? qrPanel(env, layer.id) : null;
    default:
      return null;
  }
}

// ── Small shared pieces ──────────────────────────────────────────────────────

/** The layer this panel is about, re-read on every use (undo may replace it). */
function layerOf(env: PhoneEnv, id: string): DesignLayer | null {
  return env.ctx.store.value().layers.find((layer) => layer.id === id) ?? null;
}

function patchLayer(env: PhoneEnv, id: string, changes: LayerPatch, tag?: string): void {
  env.ctx.dispatch({ type: 'update', id, patch: changes, ...(tag ? { tag } : {}) });
}

/**
 * A dispatcher for slider drags: the last value always lands, the ones in
 * between at most every 70 ms. Without the throttle a drag across a size
 * slider asks Skia for sixty rasters a second; without the trailing call the
 * design would keep whatever value the last tick happened to catch.
 */
function throttled(scope: HTMLElement, apply: (value: number) => void): { live(value: number): void; flush(value: number): void } {
  // Removing/rebuilding a control invalidates its pending value.
  const lifetime = document.createComment('live-control');
  scope.append(lifetime);
  let last = 0;
  let timer: number | undefined;
  let pending: number | undefined;
  const fire = (value: number): void => {
    last = Date.now();
    pending = undefined;
    if (lifetime.isConnected) apply(value);
  };
  return {
    live(value) {
      pending = value;
      const wait = LIVE_MS - (Date.now() - last);
      if (wait <= 0) {
        if (timer !== undefined) window.clearTimeout(timer);
        timer = undefined;
        fire(value);
        return;
      }
      if (timer !== undefined) return;
      timer = window.setTimeout(() => {
        timer = undefined;
        if (pending !== undefined) fire(pending);
      }, wait);
    },
    flush(value) {
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
      fire(value);
    },
  };
}

function labelFor(text: string): HTMLElement {
  return el('span', 'pzd-ph-label', text);
}

interface OptionItem {
  id: string;
  label: string;
  icon?: PhoneIconName;
  on?: boolean;
  disabled?: boolean;
}

/** A row of tappable options (align, filters, pattern modes, flip axes). */
function optionRow(items: OptionItem[], onPick: (id: string) => void): HTMLElement {
  const row = el('div', 'pzd-ph-options');
  row.setAttribute('role', 'group');
  for (const item of items) {
    const button = el('button', `pzd-ph-option is-${item.id}${item.on ? ' is-on' : ''}`);
    button.type = 'button';
    button.dataset.option = item.id;
    if (item.icon) {
      const icon = el('span');
      icon.innerHTML = phoneIconSvg(item.icon, 20);
      button.append(icon);
    }
    button.append(el('span', undefined, item.label));
    button.setAttribute('aria-label', item.label);
    button.setAttribute('aria-pressed', String(Boolean(item.on)));
    if (item.disabled) button.disabled = true;
    button.addEventListener('click', () => onPick(item.id));
    row.append(button);
  }
  return row;
}

interface NumberPanelSpec {
  id: string;
  title: string;
  range: ReturnType<typeof sizeRange>;
  /** Reads the shown value off the layer. */
  read(layer: DesignLayer): number;
  /** Turns the shown value into a layer patch; `layer` is the CURRENT one. */
  write(value: number, layer: DesignLayer | null): LayerPatch;
  /** Extra content under the stepper. */
  extra?(body: HTMLElement): void;
}

/** The shape almost every sub-panel has: one stepper over one model field. */
function numberPanel(env: PhoneEnv, id: string, spec: NumberPanelSpec): SubPanelSpec {
  return {
    id: spec.id,
    title: spec.title,
    build(body) {
      const layer = layerOf(env, id);
      if (!layer) return;
      const tag = `${spec.id}:${id}`;
      const pump = throttled(body, (value) => patchLayer(env, id, spec.write(value, layerOf(env, id)), tag));
      const stepper = createPhoneStepper({
        texts: env.texts,
        ariaLabel: spec.title,
        range: spec.range,
        value: spec.read(layer),
        onInput: (value) => pump.live(value),
        onCommit: (value) => pump.flush(value),
      });
      body.append(stepper.element);
      spec.extra?.(body);
      return () => stepper.destroy();
    },
  };
}

// ── Text ─────────────────────────────────────────────────────────────────────

function textPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'edit',
    title: env.texts.titleEditText,
    build(body) {
      const layer = layerOf(env, id);
      if (layer?.kind !== 'text') return;
      const max = env.ctx.config.text.maxChars;
      const area = el('textarea', 'pzd-ph-area');
      area.value = layer.text;
      area.maxLength = max;
      area.placeholder = env.ctx.texts.textPlaceholder;
      area.setAttribute('aria-label', env.texts.titleEditText);
      const counter = note(fillText(env.ctx.texts.chars, { n: layer.text.length, max }));
      area.addEventListener('input', () => {
        counter.textContent = fillText(env.ctx.texts.chars, { n: area.value.length, max });
        patchLayer(env, id, { text: area.value }, `text:${id}`);
      });
      body.append(area, counter);
      // The keyboard is the whole panel on a phone: focus it at once, the way
      // Zazzle's "Add Text" sheet does.
      window.setTimeout(() => area.focus(), 30);
    },
  };
}

function fontPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'font',
    title: env.texts.titleFont,
    build(body) {
      const layer = layerOf(env, id);
      if (layer?.kind !== 'text') return;
      const picker = safeFontPicker({
        ctx: env.ctx,
        fontId: layer.fontId,
        host: body,
        onPick: (fontId) => {
          void env.ctx.fonts.ensure(fontId).catch(() => undefined);
          patchLayer(env, id, { fontId }, `font:${id}`);
          env.ctx.invalidate(id);
        },
        onClose: () => env.showSub(null),
      });
      return () => picker.destroy();
    },
  };
}

function sizePanel(env: PhoneEnv, id: string): SubPanelSpec {
  return numberPanel(env, id, {
    id: 'size',
    title: env.texts.titleSize,
    range: sizeRange(env.ctx.config),
    read: (layer) => (layer.kind === 'text' ? layer.sizeMm : 0),
    write: (value) => ({ sizeMm: value }),
  });
}

function colorPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'color',
    title: env.texts.titleColor,
    build(body) {
      const layer = layerOf(env, id);
      if (layer?.kind !== 'text') return;
      const block = safeColorBlock({
        ctx: env.ctx,
        value: layer.color,
        palette: env.ctx.config.text.palette,
        onPick: (color) => {
          if (!color) return;
          patchLayer(env, id, { color }, `color:${id}`);
          env.refreshGrid();
        },
      });
      body.append(block.element);
      return () => block.destroy();
    },
  };
}

/**
 * "Poravnanje": two rows, the way the reference's mobile panel has them —
 * across (Levo · Sredina · Desno) and down (Gore · Sredina · Dole).
 *
 * The second row is NOT an alignment inside the box: our box is exactly as
 * tall as the lines it holds, so what the shopper picks is which edge stays
 * still when the number of lines changes. It is in this panel because that is
 * where they come looking for it.
 */
function alignPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'align',
    title: env.texts.titleAlign,
    build(body) {
      const paint = (): void => {
        body.textContent = '';
        const layer = layerOf(env, id);
        if (layer?.kind !== 'text') return;
        const current = layer.align ?? 'center';
        body.append(labelFor(env.texts.align), optionRow([
          { id: 'left', label: env.texts.alignLeft, icon: 'alignLeft', on: current === 'left' },
          { id: 'center', label: env.texts.alignCenter, icon: 'alignCenter', on: current === 'center' },
          { id: 'right', label: env.texts.alignRight, icon: 'alignRight', on: current === 'right' },
        ], (pick) => {
          patchLayer(env, id, { align: pick as 'left' | 'center' | 'right' }, `align:${id}`);
          paint();
        }));
        const anchor = anchorVOf(layer);
        body.append(labelFor(env.texts.anchorRow), optionRow([
          { id: 'top', label: env.texts.anchorTop, icon: 'alignTop', on: anchor === 'top' },
          { id: 'middle', label: env.texts.anchorMiddle, icon: 'alignMiddle', on: anchor === 'middle' },
          { id: 'bottom', label: env.texts.anchorBottom, icon: 'alignBottom', on: anchor === 'bottom' },
        ], (pick) => {
          setTextBox(env.ctx, [id], { anchorV: pick as 'top' | 'middle' | 'bottom' });
          paint();
        }));
      };
      paint();
    },
  };
}

/**
 * "Širina": the switch that makes a text a wrap column, and the column's own
 * width once it is one.
 *
 * On a phone there are no side handles worth aiming at with a thumb, so this
 * cell is the way in — and it is the way back out, which the reference editor
 * does not offer at all.
 */
function textWidthPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'textWidth',
    title: env.texts.titleTextWidth,
    build(body) {
      let stepper: { destroy(): void } | undefined;
      const paint = (): void => {
        stepper?.destroy();
        stepper = undefined;
        body.textContent = '';
        const layer = layerOf(env, id);
        if (layer?.kind !== 'text') return;
        const fixed = isFixedTextBox(layer);
        body.append(switchRow(env.texts.fixedWidth, fixed, (on) => {
          setTextBox(env.ctx, [id], {
            boxMode: on ? 'fixed' : 'auto',
            ...(on ? { widthMm: layer.box.w } : {}),
          });
          env.refreshGrid();
          paint();
        }));
        body.append(note(fixed ? env.texts.fixedWidthOn : env.texts.fixedWidthOff));
        if (!fixed) return;
        const range = wrapWidthRange(env.ctx.config, layer.box.w, minWrapWidthMm(layer.sizeMm));
        const current = () => layerOf(env, id);
        const pump = throttled(body, (value) => {
          const now = current();
          if (now) setTextBoxWidth(env.ctx, now, value, `wrap:${id}`);
        });
        const control = createPhoneStepper({
          texts: env.texts,
          ariaLabel: env.texts.wrapWidth,
          range,
          value: layer.box.w,
          onInput: (value) => pump.live(value),
          onCommit: (value) => pump.flush(value),
        });
        body.append(labelFor(env.texts.wrapWidth), control.element);
        stepper = control;
      };
      paint();
      return () => stepper?.destroy();
    },
  };
}

function strokePanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'textStroke',
    title: env.texts.titleStroke,
    build(body) {
      let stepper: { destroy(): void } | undefined;
      let block: { destroy(): void } | undefined;
      const paint = (): void => {
        stepper?.destroy();
        block?.destroy();
        body.textContent = '';
        const layer = layerOf(env, id);
        if (layer?.kind !== 'text') return;
        const outline = layer.outline;
        body.append(switchRow(env.texts.enabled, Boolean(outline), (on) => {
          patchLayer(env, id, {
            outline: on
              ? { color: env.ctx.config.text.outlinePalette[0] ?? { hex: '#000000' }, width: uiToStroke(75) }
              : undefined,
          }, `stroke:${id}`);
          paint();
        }));
        if (!outline) return;
        const colors = safeColorBlock({
          ctx: env.ctx,
          title: env.texts.strokeColor,
          value: outline.color,
          palette: env.ctx.config.text.outlinePalette,
          onPick: (color) => {
            if (!color) return;
            patchLayer(env, id, { outline: { color, width: outline.width } }, `stroke:${id}`);
          },
        });
        body.append(colors.element);
        block = colors;
        body.append(labelFor(env.texts.strokeThickness));
        const pump = throttled(body, (value) => {
          const current = layerOf(env, id);
          const width = uiToStroke(value);
          const color = current?.kind === 'text' ? current.outline?.color ?? outline.color : outline.color;
          patchLayer(env, id, { outline: { color, width } }, `stroke:${id}`);
        });
        const thickness = createPhoneStepper({
          texts: env.texts,
          ariaLabel: env.texts.strokeThickness,
          range: STROKE_RANGE,
          value: strokeToUi(outline.width),
          onInput: (value) => pump.live(value),
          onCommit: (value) => pump.flush(value),
        });
        body.append(thickness.element);
        stepper = thickness;
      };
      paint();
      return () => {
        stepper?.destroy();
        block?.destroy();
      };
    },
  };
}

function shadowPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'shadow',
    title: env.texts.titleShadow,
    build(body) {
      const parts: Array<{ destroy(): void }> = [];
      const paint = (): void => {
        for (const part of parts.splice(0)) part.destroy();
        body.textContent = '';
        const layer = layerOf(env, id);
        if (layer?.kind !== 'text') return;
        const shadow = layer.shadow;
        body.append(switchRow(env.texts.enabled, Boolean(shadow), (on) => {
          patchLayer(env, id, {
            shadow: on ? { color: { hex: '#9a9a9a' }, offset: 55, angle: 45, blur: 0 } : undefined,
          }, `shadow:${id}`);
          paint();
        }));
        if (!shadow) return;
        // Always written from the CURRENT shadow, never from the one captured
        // when the panel was painted: three steppers write into the same object.
        const current = (): { color: Color; offset: number; angle: number; blur: number } => {
          const now = layerOf(env, id);
          return now?.kind === 'text' && now.shadow ? now.shadow : shadow;
        };
        const colors = safeColorBlock({
          ctx: env.ctx,
          title: env.texts.shadowColor,
          value: shadow.color,
          palette: env.ctx.config.text.outlinePalette,
          onPick: (color) => {
            if (!color) return;
            patchLayer(env, id, { shadow: { ...current(), color } }, `shadow:${id}`);
          },
        });
        body.append(colors.element);
        parts.push(colors);
        const number = (
          title: string,
          range: typeof SHADOW_ANGLE_RANGE,
          value: number,
          write: (v: number) => { offset?: number; angle?: number; blur?: number },
        ): void => {
          body.append(labelFor(title));
          const pump = throttled(body, (next) => patchLayer(env, id, { shadow: { ...current(), ...write(next) } }, `shadow:${id}`));
          const stepper = createPhoneStepper({
            texts: env.texts,
            ariaLabel: title,
            range,
            value,
            onInput: (next) => pump.live(next),
            onCommit: (next) => pump.flush(next),
          });
          body.append(stepper.element);
          parts.push(stepper);
        };
        number(env.texts.shadowDirection, SHADOW_ANGLE_RANGE, shadow.angle, (v) => ({ angle: v }));
        number(env.texts.shadowOffset, SHADOW_OFFSET_RANGE, shadow.offset, (v) => ({ offset: v }));
        number(env.texts.shadowBlur, SHADOW_BLUR_RANGE, shadow.blur, (v) => ({ blur: v }));
      };
      paint();
      return () => {
        for (const part of parts.splice(0)) part.destroy();
      };
    },
  };
}

// ── Any layer ────────────────────────────────────────────────────────────────

function rotatePanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'rotate',
    title: env.texts.titleRotate,
    build(body) {
      const layer = layerOf(env, id);
      if (!layer) return;
      const pump = throttled(body, (value) => {
        const now = layerOf(env, id);
        if (!now) return;
        env.ctx.dispatch({ type: 'box', id, box: { ...now.box, rotation: uiToRotation(value) }, tag: `rotate:${id}` });
      });
      const stepper = createPhoneStepper({
        texts: env.texts,
        ariaLabel: env.texts.titleRotate,
        range: ROTATION_RANGE,
        value: rotationToUi(layer.box.rotation),
        onInput: (value) => pump.live(value),
        onCommit: (value) => pump.flush(value),
      });
      body.append(stepper.element);
      return () => stepper.destroy();
    },
  };
}

function flipPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'flip',
    title: env.texts.titleFlip,
    build(body) {
      const paint = (): void => {
        body.textContent = '';
        const layer = layerOf(env, id);
        if (!layer) return;
        body.append(optionRow([
          { id: 'x', label: env.texts.horizontal, icon: 'flipH', on: Boolean(layer.flipX) },
          { id: 'y', label: env.texts.vertical, icon: 'flipV', on: Boolean(layer.flipY) },
        ], (axis) => {
          const now = layerOf(env, id);
          if (!now) return;
          patchLayer(env, id, axis === 'x' ? { flipX: !now.flipX } : { flipY: !now.flipY }, `flip:${id}`);
          paint();
        }));
      };
      paint();
    },
  };
}

function arrangePanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'arrange',
    title: env.texts.titleArrange,
    build(body) {
      body.append(optionRow([
        { id: 'backward', label: env.texts.stepBackward, icon: 'down' },
        { id: 'forward', label: env.texts.stepForward, icon: 'up' },
      ], (pick) => env.ctx.dispatch({ type: pick === 'forward' ? 'raise' : 'lower', id })));
    },
  };
}

function patternPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'pattern',
    title: env.texts.titlePattern,
    build(body) {
      const steppers: Array<{ destroy(): void }> = [];
      const paint = (): void => {
        for (const part of steppers.splice(0)) part.destroy();
        body.textContent = '';
        const layer = layerOf(env, id);
        if (!layer) return;
        const pattern = layer.pattern;
        const mode = pattern?.mode;
        body.append(optionRow([
          { id: 'none', label: env.texts.patternNone, on: !pattern },
          { id: 'centered', label: env.texts.patternCentered, on: mode === 'centered' },
          { id: 'basic', label: env.texts.patternBasic, on: mode === 'basic' },
          { id: 'half-brick', label: env.texts.patternHalfBrick, on: mode === 'half-brick' },
          { id: 'half-drop', label: env.texts.patternHalfDrop, on: mode === 'half-drop' },
          { id: 'mirror', label: env.texts.patternMirror, on: mode === 'mirror' },
        ], (pick) => {
          const next: DesignerPattern | undefined = pick === 'none'
            ? undefined
            : { mode: pick as DesignerPattern['mode'], gapXmm: pattern?.gapXmm ?? 0, gapYmm: pattern?.gapYmm ?? 0 };
          patchLayer(env, id, { pattern: next }, `pattern:${id}`);
          paint();
        }));
        if (!pattern) return;
        const gap = (title: string, value: number, axis: 'x' | 'y'): void => {
          body.append(labelFor(title));
          const pump = throttled(body, (next) => {
            const now = layerOf(env, id);
            const base = now?.pattern ?? pattern;
            patchLayer(env, id, {
              pattern: { ...base, ...(axis === 'x' ? { gapXmm: next } : { gapYmm: next }) },
            }, `pattern:${id}`);
          });
          const stepper = createPhoneStepper({
            texts: env.texts,
            ariaLabel: title,
            range: PATTERN_GAP_RANGE,
            value,
            onInput: (next) => pump.live(next),
            onCommit: (next) => pump.flush(next),
          });
          body.append(stepper.element);
          steppers.push(stepper);
        };
        gap(env.texts.patternGapX, pattern.gapXmm, 'x');
        gap(env.texts.patternGapY, pattern.gapYmm, 'y');
      };
      paint();
      return () => {
        for (const part of steppers.splice(0)) part.destroy();
      };
    },
  };
}

// ── Image ────────────────────────────────────────────────────────────────────

function replacePanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'replace',
    title: env.texts.replace,
    build(body) {
      // D's Slike panel in replace mode: the next picture the shopper picks —
      // uploaded, from this session's gallery, or sent from their phone --
      // lands on THIS layer, keeping its box and dropping the stale crop.
      const stop = renderImagePanel(body, env.ctx, { replaceLayerId: id });
      return () => { stop(); endReplace(env.ctx); };
    },
  };
}

function cropPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return { id: 'crop', title: env.texts.titleCrop, build: body => renderCropControl(body, env.ctx, id) };
}

function filtersPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'filters',
    title: env.texts.titleFilters,
    build(body) {
      const parts: Array<{ destroy(): void }> = [];
      const without = (effects: DesignImageEffect[] | undefined, kinds: string[]): DesignImageEffect[] =>
        (effects ?? []).filter((effect) => !kinds.includes(effect.kind));
      const amountOf = (effects: DesignImageEffect[] | undefined, kind: 'brightness' | 'contrast'): number => {
        for (const effect of effects ?? []) if (effect.kind === kind) return effect.amount;
        return 0;
      };
      const paint = (): void => {
        for (const part of parts.splice(0)) part.destroy();
        body.textContent = '';
        const layer = layerOf(env, id);
        if (layer?.kind !== 'image') return;
        const effects = layer.effects;
        // The same look tiles as the desktop panel, through the same command.
        body.append(createLookGrid(env.ctx, layer, (look) => {
          run(env.ctx, { op: 'imageEffects', ids: [id], look }, `filters:${id}`);
          env.ctx.invalidate(id);
          paint();
        }));
        const white = (effects ?? []).find((effect) => effect.kind === 'whiteTransparent');
        const whiteMode = !white ? 'never' : 'connected' in white && white.connected ? 'border' : 'all';
        body.append(labelFor(env.ctx.texts.whiteRemoval));
        body.append(optionRow([
          { id: 'never', label: env.ctx.texts.whiteNever, on: whiteMode === 'never' },
          { id: 'border', label: env.ctx.texts.whiteBorder, on: whiteMode === 'border' },
          { id: 'all', label: env.ctx.texts.whiteAll, on: whiteMode === 'all' },
        ], (pick) => {
          run(env.ctx, { op: 'imageEffects', ids: [id], whiteRemoval: pick === 'never' ? null : pick === 'border' ? 'border' : 'all' }, `white:${id}`);
          env.ctx.invalidate(id);
          paint();
        }));
        const amount = (title: string, kind: 'brightness' | 'contrast'): void => {
          body.append(labelFor(title));
          const pump = throttled(body, (value) => {
            const now = layerOf(env, id);
            const list = now?.kind === 'image' ? now.effects : undefined;
            const rest = without(list, [kind]);
            const model = uiToFilterAmount(value);
            patchLayer(env, id, {
              effects: model === 0 ? rest : [...rest, { kind, amount: model }],
            }, `${kind}:${id}`);
            env.ctx.invalidate(id);
          });
          const stepper = createPhoneStepper({
            texts: env.texts,
            ariaLabel: title,
            range: FILTER_AMOUNT_RANGE,
            value: filterAmountToUi(amountOf(effects, kind)),
            onInput: (value) => pump.live(value),
            onCommit: (value) => pump.flush(value),
          });
          body.append(stepper.element);
          parts.push(stepper);
        };
        amount(env.texts.brightness, 'brightness');
        amount(env.texts.contrast, 'contrast');
      };
      paint();
      return () => {
        for (const part of parts.splice(0)) part.destroy();
      };
    },
  };
}

// ── Shape and artwork ────────────────────────────────────────────────────────

function shapeFillPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'shapeFill',
    title: env.texts.titleShapeFill,
    build(body) {
      const layer = layerOf(env, id);
      if (layer?.kind !== 'shape') return;
      const block = safeColorBlock({
        ctx: env.ctx,
        value: layer.fill,
        palette: env.ctx.config.shape.palette,
        allowNone: true,
        onPick: (color) => {
          patchLayer(env, id, { fill: color }, `fill:${id}`);
          env.refreshGrid();
        },
      });
      body.append(block.element);
      return () => block.destroy();
    },
  };
}

function shapeLinePanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'shapeLine',
    title: env.texts.titleShapeLine,
    build(body) {
      const layer = layerOf(env, id);
      if (layer?.kind !== 'shape') return;
      const width = layer.stroke?.widthMm ?? 0.5;
      const block = safeColorBlock({
        ctx: env.ctx,
        value: layer.stroke?.color,
        palette: env.ctx.config.shape.palette,
        allowNone: true,
        onPick: (color) => {
          patchLayer(env, id, { stroke: color ? { color, widthMm: width } : undefined }, `line:${id}`);
          env.refreshGrid();
        },
      });
      body.append(block.element);
      return () => block.destroy();
    },
  };
}

function shapeWidthPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return numberPanel(env, id, {
    id: 'shapeWidth',
    title: env.texts.titleShapeWidth,
    range: SHAPE_STROKE_RANGE,
    read: (layer) => (layer.kind === 'shape' ? layer.stroke?.widthMm ?? 0 : 0),
    // A width of zero IS "no line": the model has no stroke without a width,
    // and the colour the shopper picked is kept while they try widths.
    write: (value, layer) => ({
      stroke: value > 0
        ? { color: (layer?.kind === 'shape' ? layer.stroke?.color : undefined) ?? { hex: '#000000' }, widthMm: value }
        : undefined,
    }),
  });
}

function recolorPanel(env: PhoneEnv, id: string): SubPanelSpec {
  return {
    id: 'recolor',
    title: env.texts.titleRecolor,
    build(body) {
      let block: { destroy(): void } | undefined;
      const paint = (): void => {
        block?.destroy();
        body.textContent = '';
        const layer = layerOf(env, id);
        if (layer?.kind !== 'artwork') return;
        if (!layer.vector) {
          body.append(note(env.ctx.texts.originalColors));
          return;
        }
        body.append(switchRow(env.ctx.texts.singleColor, Boolean(layer.singleColor), (on) => {
          patchLayer(env, id, {
            singleColor: on ? env.ctx.config.text.palette[0] ?? { hex: '#000000' } : undefined,
          }, `recolor:${id}`);
          paint();
          env.refreshGrid();
        }));
        if (!layer.singleColor) {
          body.append(note(env.ctx.texts.originalColors));
          return;
        }
        const colors = safeColorBlock({
          ctx: env.ctx,
          value: layer.singleColor,
          palette: env.ctx.config.text.palette,
          onPick: (color) => {
            if (!color) return;
            patchLayer(env, id, { singleColor: color }, `recolor:${id}`);
            env.refreshGrid();
          },
        });
        body.append(colors.element);
        block = colors;
      };
      paint();
      return () => block?.destroy();
    },
  };
}

function qrPanel(env: PhoneEnv, id: string): SubPanelSpec {
  let url = '';
  return {
    id: 'qrEdit',
    title: env.texts.titleQr,
    build(body) {
      const layer = layerOf(env, id);
      if (layer?.kind !== 'qr') return;
      url = layer.url;
      const input = el('input', 'pzd-ph-input');
      input.type = 'url';
      input.value = url;
      input.placeholder = env.texts.qrPlaceholder;
      input.setAttribute('aria-label', env.texts.titleQr);
      input.addEventListener('input', () => {
        url = input.value.trim();
      });
      body.append(input, note(env.texts.qrHint));
      window.setTimeout(() => input.focus(), 30);
    },
    apply() {
      const layer = layerOf(env, id);
      if (layer?.kind !== 'qr' || !url || url === layer.url) return;
      run(env.ctx, { op: 'qr', url, id });
    },
  };
}

// ── Otkaži ───────────────────────────────────────────────────────────────────

export type RestorePlan = 'none' | 'set';

/** Snapshot comparison for legacy callers; phone sessions restore full store checkpoints. */
export function restorePlan(
  state: { answer: DesignAreaAnswer },
  snapshot: DesignAreaAnswer,
): RestorePlan {
  return state.answer === snapshot ? 'none' : 'set';
}
